import React, { useState, forwardRef } from "react";
import { cn } from "@utils/cn";
import { Text, Button, Icon } from "@ui-component-library";
import { DynamicModal } from "@components/DynamicModal";
import { useMetricData } from "../Overblick/hooks/UseMetricData";
import { useOpenSubscriptionCheckout } from "@components/Checkout/config/SubscriptionCheckout/SubscriptionCheckoutContext";

const WidgetWrapper = forwardRef(function WidgetWrapper(
  {
    title = "Example title",
    subTitle = "",
    infoContent,
    editDialog,
    className = "",
    headerClass = "",
    children,
    extraContent,
    ...props
  },
  ref
) {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openCheckout } = useOpenSubscriptionCheckout();
  const { propDfr } = useMetricData();

  const handleEditSubmit = async () => {
    if (!editDialog?.onSubmit) return;

    setIsSubmitting(true);
    try {
      await editDialog.onSubmit();
      setShowEditDialog(false);
    } catch (error) {
      console.error("Error submitting edit form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUserSubscription = (onPlus, onLocked, extraCondition = null) => {
    const hasSubscription = propDfr || extraCondition;

    return hasSubscription ? onPlus() : onLocked();
  };

  const hasInfo = !!infoContent;
  const hasEditDialog = !!editDialog;

  return (
    <>
      <div
        ref={ref}
        className={cn(
          "rounded-2xl border-solid border-1 border-[#e6e6e6] pb-4 md:pb-0",
          className
        )}
        {...props}
      >
        {/* Header */}
        <div className="flex align-center p-4 pb-2">
          <div className={cn('flex gap-2 items-center w-full', headerClass)}>
            <div className="flex flex-col">
              <div className="flex gap-2">
                <Text variant="text-sm/leading-normal/medium">{title}</Text>
                {hasInfo && (
                  <Button
                    className="p-0"
                    onClick={() => setShowInfoModal(true)}
                    size="icon"
                    variant="tertiary"
                    aria-label="Visa information"
                  >
                    <Icon name="InfoOutlined" size="medium" />
                  </Button>
                )}
              </div>
              {subTitle && <Text className="text-[#737373]" variant="text-xs/leading-normal/medium">{subTitle}</Text>}
            </div>
            
            {extraContent && extraContent}
          </div>

          {hasEditDialog && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => {
                handleUserSubscription(
                  () => setShowEditDialog(true),
                  () => { openCheckout() },
                  null
                );
              }}
            >
              {editDialog.title || "Ändra"}
            </Button>
          )}
        </div>

        {children}
      </div>

      {hasEditDialog && (
        <DynamicModal
          isOpen={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          bp={{ sm: "drawer", lg: "dialog" }}
        >
          <DynamicModal.Header>

            <div className="flex w-full items-center justify-between">
              <Text variant="text-sm/leading-normal/medium">
                {editDialog.title || "Ändra"}
              </Text>
              <Button
                variant="tertiary"
                size="icon"
                onClick={() => setShowEditDialog(false)}
                aria-label="Stäng"
              >
                <Icon
                  name="CrossSimple"
                  className="color-charcoal-500"
                  size="large"
                />
              </Button>
            </div>
          </DynamicModal.Header>
          <DynamicModal.Content>
            {editDialog.content?.(isSubmitting)}  </DynamicModal.Content>
          <DynamicModal.Footer>

            <Button
              variant="primary"
              onClick={handleEditSubmit}
              disabled={isSubmitting || !editDialog.onSubmit}
              fullWidth
            >
              {editDialog.submitLabel || "Spara"}
              {isSubmitting && (
                <div className="size-4 border-2 border-white border-t-transparent border-solid rounded-full animate-spin"></div>
              )}
            </Button>
          </DynamicModal.Footer>
        </DynamicModal>

      )}

      {/* Info Modal */}
      {hasInfo && (
        <DynamicModal
          isOpen={showInfoModal}
          onClose={() => setShowInfoModal(false)}
          bp={{ sm: "drawer", lg: "sheet" }}
        >
          <DynamicModal.Header>
            <div className="flex justify-between w-full">
              <Text variant="text-lg/leading-normal/medium">{title}</Text>
            </div>
          </DynamicModal.Header>
          <DynamicModal.Content>
            {infoContent}
          </DynamicModal.Content>
          <DynamicModal.Footer>
            <Button
              variant="primary"
              fullWidth
              onClick={() => setShowInfoModal(false)}>
              Stäng
            </Button>
          </DynamicModal.Footer>
        </DynamicModal>

      )}
    </>
  );
});

export default WidgetWrapper;
